import { defaultTheme } from 'react-admin';

// Palette extracted from design-source/assets/styles.css :root.
// Keep values in sync with web/src/assets/styles.css if the design changes.
const TOKENS = {
  green900: '#0e2a1f',
  green800: '#163a2c',
  green700: '#1d4d3a',
  green:    '#25634a',
  greenSoft:'#3f8064',
  gold700:  '#9a7818',
  gold:     '#c19a3a',
  goldSoft: '#e0c074',
  goldPale: '#f4e7c2',
  ink:      '#14181a',
  ink2:     '#2e352e',
  ink3:     '#5d6660',
  ink4:     '#8a948c',
  line:     '#e6e1d4',
  line2:    '#efeadc',
  cream:    '#faf6ec',
  cream2:   '#f3ecd9',
  cream3:   '#ebe2c7',
  paper:    '#ffffff',
  serif:    '"Playfair Display", "Cormorant Garamond", Georgia, serif',
};

export const tokens = TOKENS;

export const mlisTheme = {
  ...defaultTheme,
  palette: {
    primary:   { main: TOKENS.green700, dark: TOKENS.green900, light: TOKENS.greenSoft, contrastText: TOKENS.cream },
    secondary: { main: TOKENS.gold,     dark: TOKENS.gold700,  light: TOKENS.goldSoft,  contrastText: TOKENS.ink },
    background:{ default: TOKENS.cream, paper: TOKENS.paper },
    text:      { primary: TOKENS.ink,   secondary: TOKENS.ink3 },
    divider:   TOKENS.line,
    success:   { main: TOKENS.green700 },
    warning:   { main: TOKENS.gold700 },
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    h1: { fontFamily: TOKENS.serif, fontWeight: 500 },
    h2: { fontFamily: TOKENS.serif, fontWeight: 500 },
    h3: { fontFamily: TOKENS.serif, fontWeight: 500 },
    h4: { fontFamily: TOKENS.serif, fontWeight: 500 },
    h5: { fontFamily: TOKENS.serif, fontWeight: 500 },
    h6: { fontFamily: TOKENS.serif, fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
    overline: { letterSpacing: '0.18em', fontWeight: 600 },
  },
  shape: { borderRadius: 6 },
  components: {
    ...(defaultTheme.components || {}),
    MuiAppBar: {
      defaultProps: { color: 'transparent', elevation: 0 },
      styleOverrides: {
        root: {
          background: TOKENS.green900,
          color: TOKENS.cream,
          boxShadow: 'none',
          borderBottom: `2px solid ${TOKENS.gold}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          paddingInline: 18,
          paddingBlock: 8,
        },
        containedPrimary: {
          backgroundColor: TOKENS.gold,
          color: TOKENS.ink,
          '&:hover': { backgroundColor: TOKENS.gold700, color: '#fff' },
        },
        outlinedPrimary: {
          borderColor: TOKENS.green700,
          color: TOKENS.green700,
          '&:hover': { backgroundColor: TOKENS.cream2 },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${TOKENS.line}`,
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 8 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: TOKENS.paper,
          '& fieldset': { borderColor: TOKENS.line },
          '&:hover fieldset': { borderColor: TOKENS.greenSoft },
          '&.Mui-focused fieldset': { borderColor: TOKENS.green700, borderWidth: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: TOKENS.ink3 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: TOKENS.cream,
          '& th': {
            color: TOKENS.ink2,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { background: TOKENS.cream2 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    // React Admin specific overrides
    RaSidebar: {
      styleOverrides: {
        root: {
          background: TOKENS.paper,
          borderRight: `1px solid ${TOKENS.line}`,
        },
      },
    },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginInline: 8,
          marginBlock: 2,
          color: TOKENS.ink2,
          '&:hover': { background: TOKENS.cream2, color: TOKENS.green700 },
          '&.RaMenuItemLink-active': {
            background: TOKENS.cream,
            color: TOKENS.green800,
            fontWeight: 600,
            borderLeft: `3px solid ${TOKENS.gold}`,
            paddingLeft: 13,
          },
        },
      },
    },
    RaLayout: {
      styleOverrides: {
        root: {
          background: TOKENS.cream,
          '& .RaLayout-content': { padding: 24 },
        },
      },
    },
    RaList: {
      styleOverrides: {
        root: {
          '& .RaList-content': {
            border: `1px solid ${TOKENS.line}`,
            borderRadius: 8,
            background: TOKENS.paper,
          },
        },
      },
    },
  },
};
